import React, { PropsWithChildren } from 'react'
import { createContext } from 'lib/createContext'
import { useCreateRuleMutation } from 'queries/wage/useCreateRuleMutation'
import { useJurisdictionsQuery } from 'queries/wage/useJurisdictionsQuery'
import { parseErrorMessage } from 'api'
import { formatJurisdictionOptionLabel } from 'pages/Wage/utils'

type JurisdictionOption = { label: string; value: number }

type CreateRuleFlowContextState = {
  state: {
    jurisdictionOptions: JurisdictionOption[]
    jurisdictionsState: {
      isLoading: boolean
      isError: boolean
    }
    mutationState: {
      isPending: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    submit: ReturnType<typeof useCreateRuleMutation>['mutate']
  }
}

const [Context, useCreateRuleFlowContextValue] =
  createContext<CreateRuleFlowContextState>({
    name: 'CreateRuleFlowContext',
  })

export const CreateRuleFlowProvider = ({ children }: PropsWithChildren) => {
  const { mutate, isPending, error } = useCreateRuleMutation()
  const {
    data: jurisdictions,
    isLoading: isJurisdictionsLoading,
    isError: isJurisdictionsError,
  } = useJurisdictionsQuery()

  const jurisdictionOptions: JurisdictionOption[] = (jurisdictions ?? []).map(
    (j) => ({ label: formatJurisdictionOptionLabel(j), value: j.id })
  )

  const value: CreateRuleFlowContextState = {
    state: {
      jurisdictionOptions,
      jurisdictionsState: {
        isLoading: isJurisdictionsLoading,
        isError: isJurisdictionsError,
      },
      mutationState: {
        isPending,
        errorMessage: parseErrorMessage(error),
      },
    },
    actions: { submit: mutate },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useCreateRuleFlowContextValue }
